import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2020 semantics GmbH
//  Date      : $Date: 2026-02-19 12:26:35 +0100 (Thu, 19 Feb 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 160857 $
//  Purpose   : import jobs
// =============================================================================
// ---Third-party imports
import React from 'react';
import { List } from 'semantic-ui-react';
// ---Framework imports
import log from 'uix/logging';
import { sum, uniq, mapItems, flatten, sorted, size, dict, items } from 'ldash';
import { prettyBytes } from 'uix/utils';
import { Link } from 'uix/components';
import { TypeIcon } from 'uix/types/components';
import { useSelector, useMemo } from 'uix/hooks';
import { Sel } from 'uix/registries';
import { TranslatedTable } from 'uix/components';
import { useTreeNodes } from 'uix/db/hooks';
import { formatDateTime, formatTime } from 'uix/utils/date';
// ---Exports
const MetadataLink = ({ i18n, otid, subtype, caption })=>/*#__PURE__*/ _jsxs(Link, {
        title: i18n.getType(1, subtype),
        classname: "tree",
        tabname: "id",
        id: otid,
        children: [
            /*#__PURE__*/ _jsx(TypeIcon, {
                datatype: "1",
                subtype: subtype
            }),
            " ",
            caption || otid,
            ' '
        ]
    });
let FILE_HEADERS = {
    image: 'Image',
    pdf: 'PDF',
    ft: 'Fulltext'
};
const KNOWN_FILES = [
    'image',
    'pdf',
    'ft'
];
export const ImportJob = {
    compact: ({ eventTypes })=>{
        //~ vl.pprint(eventTypes)
        let i18n = useSelector(Sel.i18n);
        let md_otids = items(dict([
            ...eventTypes['metadata update'] || [],
            ...eventTypes['metadata insert'] || []
        ].map((e)=>[
                e.otID,
                e.doctype
            ])));
        if (md_otids.length == 0) {
            return 'no documents affected';
        } else if (md_otids.length == 1) {
            let [otid, subtype] = md_otids[0];
            return /*#__PURE__*/ _jsx(MetadataLink, {
                i18n: i18n,
                subtype: subtype,
                otid: otid
            });
        } else {
            let [otid, subtype] = md_otids[0];
            return /*#__PURE__*/ _jsxs("span", {
                children: [
                    /*#__PURE__*/ _jsx("b", {
                        children: md_otids.length
                    }),
                    ` Titles affected`
                ]
            });
        }
    },
    report: ({ events, eventTypes, ojid })=>{
        let apiurl = useSelector(Sel.apiurl);
        //~ let events = sorted(
        //~ flatten(mapItems(eventTypes, (type, events) => events.map(e => ({...e, type}))), e => e.time)
        //~ )
        let i18n = useSelector(Sel.i18n);
        let { packages, md_otids } = useMemo(()=>{
            let data = null;
            let packages = [];
            events.forEach((e)=>{
                //~ log.x(e.time)
                if (e.type == 'subjob created') {
                    data = {
                        time: e.time,
                        name: e.metsfile.slice(0, -5),
                        warning: [],
                        error: [],
                        md_otids: []
                    };
                    packages.push(data);
                } else if (data) {
                    if (e.type == 'warning') data.warning.push(e);
                    else if (e.type == 'error') data.error.push(e);
                    else if (e.type == 'metadata stats') data.metadata_stats = e;
                    else if (e.type == 'metadata struct import stats') data.structure_stats = e;
                    else if (e.type == 'fptr import stats') data.fptr_stats = e;
                    else if (e.type == 'link import stats') data.link_stats = e;
                    else if (e.type == 'metadata update') data.md_otids.push(e.otID);
                    else if (e.type == 'metadata insert') data.md_otids.push(e.otID);
                }
            });
            let md_otids = uniq(flatten(packages.map((data)=>data.md_otids)));
            return {
                packages,
                md_otids
            };
        }, [
            events
        ]);
        let otid2node = useTreeNodes(md_otids);
        let columns = [
            {
                id: 'time',
                Header: 'Time',
                Cell: ({ value })=>formatTime(value),
                accessor: 'time',
                width: 75,
                sortable: true
            },
            {
                Header: 'Package',
                id: 'name',
                accessor: 'name'
            },
            {
                Header: 'Metadata',
                columns: [
                    {
                        Header: 'Affected',
                        id: 'affected',
                        Cell: ({ value })=>{
                            let nodes = uniq(value).map((otid)=>otid2node[otid]).filter((e)=>e);
                            let links = nodes.slice(0, 3).map((n)=>/*#__PURE__*/ _jsx(MetadataLink, {
                                    i18n: i18n,
                                    subtype: n.subtype,
                                    otid: n.otid,
                                    caption: n.caption
                                }, n.otid));
                            return links;
                        },
                        accessor: 'md_otids'
                    },
                    {
                        id: 'md_insert',
                        Header: 'Insert',
                        width: 60,
                        accessor: (f)=>f.metadata_stats?.newMetadata
                    },
                    {
                        id: 'md_update',
                        Header: 'Update',
                        width: 60,
                        accessor: (f)=>f.metadata_stats?.updatedMetadata
                    }
                ]
            },
            {
                Header: 'Structure',
                columns: [
                    {
                        id: 'struct_insert',
                        Header: 'TOC',
                        width: 60,
                        accessor: (f)=>f.structure_stats ? f.structure_stats.insertedStructs - f.structure_stats.newPages : 0
                    },
                    {
                        id: 'page_insert',
                        Header: 'Pages',
                        width: 60,
                        accessor: (f)=>f.structure_stats?.newPages
                    },
                    {
                        id: 'link_insert',
                        Header: 'Links',
                        width: 60,
                        accessor: (f)=>f.link_stats?.insertedLinks ?? 0
                    }
                ]
            }
        ];
        let file_cols = useMemo(()=>KNOWN_FILES.filter((n)=>packages.some((f)=>f.fptr_stats?.[`${n}Count`])).map((n)=>({
                    id: `file_${n}`,
                    Header: FILE_HEADERS[n] || n,
                    width: 60,
                    accessor: (f)=>f.fptr_stats?.[`${n}Count`]
                })), [
            packages
        ]);
        if (file_cols.length > 0) {
            columns = [
                ...columns,
                {
                    Header: 'Resources',
                    columns: file_cols
                }
            ];
        }
        if (size(eventTypes.error) + size(eventTypes.warning) > 0) {
            columns = [
                ...columns,
                {
                    Header: 'Warning / Error',
                    columns: [
                        {
                            id: 'warning',
                            Header: 'W',
                            width: 60,
                            accessor: (f)=>f.warning.length
                        },
                        {
                            id: 'error',
                            Header: 'E',
                            width: 60,
                            accessor: (f)=>f.error.length
                        }
                    ]
                }
            ];
        }
        columns = [
            ...columns,
            {
                id: 'links',
                Header: 'Links',
                width: 120,
                Cell: ({ original })=>{
                    let impfile = (name)=>/*#__PURE__*/ _jsx("a", {
                            style: {
                                marginRight: '2px'
                            },
                            href: apiurl.href(`/api/getImportBackupFile?package_id=${ojid}_${original.name}&name=${name}`),
                            target: "impfile",
                            children: name
                        });
                    return /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            impfile('log'),
                            impfile('mets'),
                            impfile('stats.txt')
                        ]
                    });
                }
            }
        ];
        let showPagination = packages.length > 20;
        return /*#__PURE__*/ _jsx(TranslatedTable, {
            columns: columns,
            data: packages,
            showPagination: showPagination,
            showPaginationTop: true,
            showPaginationBottom: false,
            pageSizeOptions: [
                20,
                100,
                500,
                1000
            ],
            defaultPagesize: 50,
            showCount: true,
            defaultSorted: [
                {
                    id: 'time',
                    desc: true
                }
            ]
        }, 'tt_' + String(showPagination));
    }
};
